"use client";

import { useState, useSyncExternalStore } from "react";
import "./packlist.css";

export type ChecklistGroup = {
  name: string;
  items: { id: number; text: string; note: string; product: { href: string; title: string } | null }[];
};
type Own = { id: string; text: string; done: boolean };
type Saved = { checked: number[]; own: Own[] };

const EMPTY: Saved = { checked: [], own: [] };
const MAX_OWN = 50;
const storeKey = (slug: string) => `gcg-pack:${slug}`;
const EVENT = "gcg-pack";

const cache = new Map<string, { raw: string | null; value: Saved }>();
function read(slug: string): Saved {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storeKey(slug));
  } catch {
    // Speicher gesperrt: Liste funktioniert, merkt sich aber nichts.
  }
  const hit = cache.get(slug);
  if (hit && hit.raw === raw) return hit.value;
  let value = EMPTY;
  try {
    const d = raw ? (JSON.parse(raw) as Partial<Saved>) : null;
    if (d) {
      value = {
        checked: Array.isArray(d.checked) ? d.checked.filter((n) => Number.isInteger(n)).slice(0, 500) : [],
        own: Array.isArray(d.own)
          ? d.own.filter((o) => typeof o?.id === "string" && typeof o.text === "string").slice(0, MAX_OWN).map((o) => ({ id: o.id, text: o.text.slice(0, 160), done: !!o.done }))
          : [],
      };
    }
  } catch {
    value = EMPTY;
  }
  cache.set(slug, { raw, value });
  return value;
}
function write(slug: string, v: Saved) {
  try {
    localStorage.setItem(storeKey(slug), JSON.stringify({ v: 1, ...v }));
  } catch {
    return;
  }
  window.dispatchEvent(new Event(EVENT));
}
function subscribe(cb: () => void) {
  window.addEventListener("storage", cb);
  window.addEventListener(EVENT, cb);
  return () => {
    window.removeEventListener("storage", cb);
    window.removeEventListener(EVENT, cb);
  };
}

let ownSeq = 0;
const ownId = () => `o${Date.now().toString(36)}${(++ownSeq).toString(36)}`;

/** Abhakbare Packliste. Ohne JavaScript ein normales Formular, das ans PDF geschickt wird. */
export function Checklist({ slug, groups, pdfAction }: { slug: string; groups: ChecklistGroup[]; pdfAction: string }) {
  const saved = useSyncExternalStore(subscribe, () => read(slug), () => null);
  const hydrated = saved !== null;
  const s = saved ?? EMPTY;
  const checked = new Set(s.checked);
  const [openOnly, setOpenOnly] = useState(false);
  const [draft, setDraft] = useState("");
  const [msg, setMsg] = useState("");

  const all = groups.flatMap((g) => g.items);
  const known = new Set(all.map((i) => i.id));
  const done = all.filter((i) => checked.has(i.id)).length + s.own.filter((o) => o.done).length;
  const total = all.length + s.own.length;

  const set = (patch: Partial<Saved>) => write(slug, { ...s, ...patch });
  const toggle = (id: number, on: boolean) => set({ checked: on ? [...s.checked.filter((x) => known.has(x)), id] : s.checked.filter((x) => x !== id) });
  const addOwn = () => {
    const text = draft.trim().slice(0, 160);
    if (!text) return;
    if (s.own.length >= MAX_OWN) return setMsg(`Höchstens ${MAX_OWN} eigene Einträge.`);
    set({ own: [...s.own, { id: ownId(), text, done: false }] });
    setDraft("");
    setMsg(`„${text}“ hinzugefügt.`);
  };

  return (
    <form className="pk" method="post" action={pdfAction}>
      <div className="pk-bar">
        <div className="pk-progress">
          <label htmlFor="pk-progress">{hydrated ? `${done} von ${total} erledigt` : `${total} Einträge`}</label>
          <progress id="pk-progress" max={total || 1} value={done} />
        </div>
        <div className="pk-actions">
          <button type="submit" className="tbutton">Als PDF herunterladen</button>
          {hydrated && (
            <>
              <button type="button" className="tbutton pk-ghost" aria-pressed={openOnly} onClick={() => setOpenOnly((x) => !x)}>
                {openOnly ? "Alle anzeigen" : "Nur offene anzeigen"}
              </button>
              <button type="button" className="tbutton pk-ghost" disabled={!done} onClick={() => {
                if (!confirm("Alle Häkchen entfernen? Eigene Einträge bleiben erhalten.")) return;
                set({ checked: [], own: s.own.map((o) => ({ ...o, done: false })) });
                setMsg("Alle Häkchen entfernt.");
              }}>Häkchen zurücksetzen</button>
            </>
          )}
        </div>
        <p className="pk-saved">{hydrated ? "Häkchen und eigene Einträge werden nur in diesem Browser gespeichert." : "Abhaken und als PDF herunterladen."}</p>
      </div>
      <p className="sr-only" role="status">{msg}</p>

      {groups.map((g, gi) => {
        const items = openOnly ? g.items.filter((i) => !checked.has(i.id)) : g.items;
        const gDone = g.items.filter((i) => checked.has(i.id)).length;
        return (
          <fieldset key={gi} className="pk-group">
            <legend>
              {g.name || "Allgemein"} {hydrated && <span className="pk-count">{gDone}/{g.items.length}</span>}
            </legend>
            {openOnly && g.items.filter((i) => checked.has(i.id)).map((i) => <input key={i.id} type="hidden" name="c" value={i.id} />)}
            {items.length === 0 ? <p className="pk-alldone">Alles eingepackt.</p> : (
              <ul>
                {items.map((i) => (
                  <li key={i.id} className={checked.has(i.id) ? "done" : undefined}>
                    <label className="pk-item">
                      <input type="checkbox" name="c" value={i.id} checked={checked.has(i.id)} onChange={(e) => toggle(i.id, e.target.checked)} />
                      <span className="pk-text">
                        {i.text}
                        {i.note && <span className="pk-note">{i.note}</span>}
                      </span>
                    </label>
                    {i.product && (
                      <p className="pk-reco">
                        Empfehlung: <a href={i.product.href} rel="sponsored nofollow noopener" target="_blank">{i.product.title}</a>
                        <span className="pk-ad"> (Anzeige)</span>
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </fieldset>
        );
      })}

      <fieldset className="pk-group pk-own">
        <legend>Eigene Einträge {hydrated && s.own.length > 0 && <span className="pk-count">{s.own.filter((o) => o.done).length}/{s.own.length}</span>}</legend>
        {hydrated ? (
          <>
            {s.own.length > 0 && (
              <ul>
                {(openOnly ? s.own.filter((o) => !o.done) : s.own).map((o) => (
                  <li key={o.id} className={o.done ? "done" : undefined}>
                    <label className="pk-item">
                      <input type="checkbox" checked={o.done} onChange={(e) => set({ own: s.own.map((x) => (x.id === o.id ? { ...x, done: e.target.checked } : x)) })} />
                      <span className="pk-text">{o.text}</span>
                    </label>
                    <button type="button" className="pk-remove" aria-label={`${o.text} entfernen`} onClick={() => {
                      set({ own: s.own.filter((x) => x.id !== o.id) });
                      setMsg(`„${o.text}“ entfernt.`);
                    }}>×</button>
                  </li>
                ))}
              </ul>
            )}
            {s.own.map((o) => <input key={o.id} type="hidden" name="o" value={`${o.done ? 1 : 0}|${o.text}`} />)}
            <div className="pk-add">
              <label htmlFor="pk-new" className="sr-only">Eigenen Eintrag hinzufügen</label>
              <input id="pk-new" value={draft} maxLength={160} placeholder="z. B. Ladekabel Kamera" onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addOwn();
                  }
                }} />
              <button type="button" className="tbutton" onClick={addOwn} disabled={!draft.trim()}>Hinzufügen</button>
            </div>
          </>
        ) : (
          <label className="pk-owntext">Einer pro Zeile, erscheinen im PDF
            <textarea name="ot" rows={4} maxLength={4000} />
          </label>
        )}
      </fieldset>
    </form>
  );
}
